/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';

import { StyledModal } from 'sarsaparilla';

import { externalAccountApiCleanup, type PostLoginResponseBase } from '../../api/account';
import { useTypedDispatch } from '../../redux';
import { writeSession } from '../authSession';

import {
    type AuthModalState,
    type AuthScreen,
    closeAuthModal,
    setScreen as setScreenAction,
} from '../authModal.slice';

import { AuthModalContext } from './AuthModalContext';
import { LoginForm } from './login/LoginForm';
import { NewAccountForms } from './newAccount/NewAccountForms';
import { ForgotPasswordForms } from './forgotPassword/ForgotPasswordForms';
import { MfaCodeForms } from './mfaCode/MfaCodeForms';
import { AccountServiceDisabledScreen } from './accountServiceDisabled/AccountServiceDisabledScreen';
import { useGetAccountStatusQuery } from '../../api/account/account.queries';
import { isAuthScreen, maybePerformRedirect } from './utils/AuthModal.utils';

import styles from './AuthModal.module.scss';

type ReduxPartial = {
    authModal: AuthModalState;
};

export interface AuthModalProps {
    /**
     * Optional callback invoked once per login-completing path (password login,
     * guest, MFA, in-modal account creation) immediately after the standard
     * login-success handling. Consumers (e.g. RIDB) use this to run their own
     * post-login hydration. Omitting it leaves behavior unchanged.
     */
    onLoginSuccess?: (session: PostLoginResponseBase) => void;
}

export function AuthModal({ onLoginSuccess }: AuthModalProps = {}) {
    const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = React.useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useTypedDispatch();
    const authModalState = useSelector((state: ReduxPartial) => state.authModal);

    const screenFromParams = isAuthScreen(searchParams.get('auth'))
        ? (searchParams.get('auth') as AuthScreen)
        : null;
    const authStep = searchParams.get('authStep');

    // A value from the URL overrides the redux state so we can support refreshing the
    // page without losing the current UI state
    const screen = screenFromParams ?? authModalState?.screen ?? null;

    // Reduce chance of accidental close by requiring a confirmation
    const makeHarderToClose = screen === 'createAccount' && Boolean(authStep);

    //------------------------------------------------------------------------------------
    // Fetch account status
    //------------------------------------------------------------------------------------
    // Omission of loading and error states is intentional.
    // Assume everything is working as long as enabled explicitly returns false.
    const { data: accountStatus } = useGetAccountStatusQuery();

    const clearAuthUrlParams = React.useCallback(() => {
        setSearchParams(
            (prev) => {
                prev.delete('auth');
                prev.delete('authStep');
                prev.delete('authEmail');
                return prev;
            },
            {
                replace: true,
            }
        );
    }, [setSearchParams]);

    //------------------------------------------------------------------------------------
    // Clean up URL parameters and temporary session storage used to maintain state between refreshes
    const cleanUpParamsAndStorage = React.useCallback(() => {
        externalAccountApiCleanup();
        clearAuthUrlParams();
    }, [clearAuthUrlParams]);

    const closeModal = React.useCallback(
        ({ preventRedirect }: { preventRedirect?: boolean } = {}) => {
            setIsCloseConfirmationOpen(false);
            cleanUpParamsAndStorage();
            dispatch(closeAuthModal());

            if (!preventRedirect) {
                // Check for a redirect search param, sanitize it and redirect if it's valid
                maybePerformRedirect();
            }
        },
        [cleanUpParamsAndStorage, dispatch]
    );

    const storeUserData = React.useCallback(
        (userData: PostLoginResponseBase) => {
            writeSession(dispatch, userData);
            // Bridge for consumers that need post-login hydration (e.g. RIDB).
            onLoginSuccess?.(userData);
        },
        [dispatch, onLoginSuccess]
    );

    const contextValue = React.useMemo(
        () => ({
            screen: authModalState?.screen ?? null,
            setScreen: (newScreen: AuthScreen) => {
                dispatch(setScreenAction(newScreen));
                // URL params take priority over redux so we must clear them
                clearAuthUrlParams();
            },
            closeModal,
            storeUserData,
            cleanUpParamsAndStorage,
            isCloseConfirmationOpen,
            setIsCloseConfirmationOpen,
        }),
        [
            authModalState?.screen,
            dispatch,
            clearAuthUrlParams,
            closeModal,
            storeUserData,
            cleanUpParamsAndStorage,
            isCloseConfirmationOpen,
        ]
    );

    //------------------------------------------------------------------------------------
    // Return UI
    //------------------------------------------------------------------------------------
    return (
        <AuthModalContext.Provider value={contextValue}>
            <StyledModal
                isOpen={screen !== null}
                size="md"
                onRequestClose={() => {
                    if (makeHarderToClose) {
                        setIsCloseConfirmationOpen(true);
                    } else {
                        closeModal();
                    }
                }}
                shouldCloseOnOverlayClick={!makeHarderToClose}
                shouldCloseOnEsc={!makeHarderToClose}
                contentClassName={styles.authModalContent}
                hasOpenInnerModal={isCloseConfirmationOpen}
            >
                {accountStatus?.enabled === false ? (
                    <AccountServiceDisabledScreen notice={accountStatus.login_notice} />
                ) : screen === 'login' ? (
                    <LoginForm isGuestAllowed={authModalState?.isGuestAllowed ?? false} />
                ) : screen === 'createAccount' ? (
                    <NewAccountForms />
                ) : screen === 'requestPasswordReset' ? (
                    <ForgotPasswordForms />
                ) : screen === 'mfaCode' ? (
                    <MfaCodeForms />
                ) : null}
            </StyledModal>
        </AuthModalContext.Provider>
    );
}
